// Shared swarm-template catalog — mirrors the PHP `SwarmTemplate` class in
// includes/additions.php (~L6120). The four swarm items live in the items
// table with `item_type = 'creature_template'`; on the compiled creature
// `$item_arr`, a swarm shows up as a UUID appended to the `creature_template`
// layer list (driven by `apply_monster_template_overrides` →
// `merge_dynamic_classification_layers_into_item_arr`). Reading from
// `creature_template[]` matches every frontend surface — DB column
// `encounter_creature_monster_template_id` is only the trigger; the
// post-compile canonical view is the layer list.
//
// `monsterQty` is how many individuals the token represents; `baseGhosts` is
// the max ghost portraits the swarm-ghost overlay renders around the leader
// at full Vitality (capped at 8 — past that the visual stops parsing as
// "more" and just burns DOM nodes). `widthLanes` is the CATALOG DEFAULT for
// the `swarm_width_lanes` item_layer_type (2026-07-13): the max EXTRA
// horizontal ground lanes the token occupies at full strength — a value
// authored on the swarm template item (or the creature itself) overrides it.
// Mirror of `SwarmTemplate` + ft_swarm_occupied_width_ft() in PHP.

import type { EncounterCreatureRow } from './array-types';
import { computedValue } from './array-types';

export type SwarmTier = 'swarm_5' | 'swarm_20' | 'swarm_50' | 'swarm_200';

export interface SwarmTemplateDef {
    id:         string;
    monsterQty: number;
    baseGhosts: number;
    widthLanes: number;
}

export const SWARM_TEMPLATES: Record<SwarmTier, SwarmTemplateDef> = {
    swarm_5:   { id: 'C721A8A3-C301-AE2B-B388-D73FE51F65CC', monsterQty: 5,   baseGhosts: 2, widthLanes: 1 },
    swarm_20:  { id: 'B93C548F-95A2-14A0-129C-C6D40B38C7CB', monsterQty: 20,  baseGhosts: 4, widthLanes: 2 },
    swarm_50:  { id: 'BD05AC4E-43DC-BC8F-E5A9-D9AA454A9D2B', monsterQty: 50,  baseGhosts: 6, widthLanes: 3 },
    swarm_200: { id: '1BE8C9F6-F3E8-2618-7FCC-1956C6C15139', monsterQty: 200, baseGhosts: 8, widthLanes: 4 },
};

export const SWARM_TEMPLATE_BY_ID: Record<string, SwarmTemplateDef & { tier: SwarmTier }> =
    Object.fromEntries(
        (Object.entries(SWARM_TEMPLATES) as Array<[SwarmTier, SwarmTemplateDef]>)
            .map(([tier, def]) => [def.id, { ...def, tier }])
    );

/** The same count/tier stamp for first compilation and subsequent saved-row deltas. */
export function swarmMemberFields(members: number, start: number) {
    return { swarm_members: members, swarm_members_start: start,
        swarm_tier: members < 2 ? null : Object.values(SWARM_TEMPLATES).map(row => row.monsterQty)
            .filter(count => count <= members).sort((a, b) => b - a)[0] ?? 2 };
}

/**
 * Shape of one row in a compiled `creature_template[]` layer list. PHP
 * `apply_monster_template_override_effect_rows()` appends either a scalar
 * string (UUID, optionally `UUID|qty`) or a structured row carrying
 * `item_layer_effect` / `item_id`. JS-side detection mirrors
 * `layer_list_entry_uuid()` in includes/functions/janus/items/.
 */
type LayerListEntry = string | { item_layer_effect?: string; item_id?: string };

function layerListEntryUuid(entry: unknown): string {
    if (typeof entry === 'string') {
        return entry.indexOf('|') === -1 ? entry : entry.split('|', 2)[0]!;
    }
    if (entry && typeof entry === 'object') {
        const e = entry as { item_layer_effect?: unknown; item_id?: unknown };
        const eff = e.item_layer_effect;
        if (typeof eff === 'string' && eff !== '') {
            return eff.indexOf('|') === -1 ? eff : eff.split('|', 2)[0]!;
        }
        const iid = e.item_id;
        if (typeof iid === 'string' && iid !== '') {
            return iid.indexOf('|') === -1 ? iid : iid.split('|', 2)[0]!;
        }
    }
    return '';
}

/**
 * Returns the swarm definition for a compiled creature row, or null if not a
 * swarm. Reads from `entity.creature_template[]` — the post-compile layer
 * list that catches every path a swarm UUID can land on a creature (direct
 * monster template, derived passives, future layer sources).
 */
export function getSwarmTemplateForCreature(
    entity: EncounterCreatureRow | null | undefined,
): (SwarmTemplateDef & { tier: SwarmTier }) | null {
    // [[project_item_arr_bucket_reshape]]: the compiled creature's layer lists live under
    // `item_layer_types[<name>]`, NOT a bare top-level key — reading only `entity.creature_template`
    // came back undefined post-reshape, so every swarm went undetected and the swarm-ghost overlay
    // never rendered. Read the bucket first; fall back to the legacy top-level slot for older blobs.
    const ec = entity as {
        creature_template?: ReadonlyArray<LayerListEntry>;
        item_layer_types?: { creature_template?: ReadonlyArray<LayerListEntry> };
    } | null | undefined;
    const list = ec?.item_layer_types?.creature_template ?? ec?.creature_template;
    if (!list || list.length === 0) return null;

    for (let i = 0; i < list.length; i++) {
        const uid = layerListEntryUuid(list[i]);
        if (uid && SWARM_TEMPLATE_BY_ID[uid]) {
            return SWARM_TEMPLATE_BY_ID[uid]!;
        }
    }
    return null;
}

// ── Swarm occupied-width rule (2026-07-13) ──────────────────────────────────
// A swarm token keeps the INDIVIDUAL's height and spreads HORIZONTALLY:
//   occupiedWidthFt = baseFt + activeExtraLanes * min(baseFt, 5)
//   activeExtraLanes = q <= 1 ? 0 : ceil(lanes * q / monsterQty)
//   q (remaining individuals) = ratio <= 0 ? 0 : max(1, ceil(monsterQty * ratio))
// where ratio = v_current / effective_v_max — deliberately NOT v_bar_v_percent,
// whose denominator includes temp vitality (a temp-V buff would wrongly narrow
// the band). Stepped (per lane), so collision/AOE geometry doesn't churn on
// every point of damage. MUST stay in lockstep with PHP
// ft_swarm_occupied_width_ft() (janus/encounter/swarm_occupied_width.php).

function readScalarInt(v: unknown): number | null {
    const raw = Array.isArray(v) ? v[0] : v;
    const n = parseInt(String(raw ?? ''), 10);
    return Number.isFinite(n) ? n : null;
}

/**
 * Max extra lanes for this swarm — host-authored `swarm_width_lanes` wins,
 * then the swarm template carrier's authored value (full-nested entries
 * carry their own item_layer_types), then the catalog default. Mirrors the
 * PHP layer_value() host_wins carrier walk closely enough for parity: bare
 * UUID carrier entries can't be descended client-side, so those fall to the
 * catalog default (which the template author should keep in sync anyway).
 */
export function swarmWidthLanesForCreature(
    entity: EncounterCreatureRow | null | undefined,
    def: SwarmTemplateDef,
): number {
    const ec = entity as {
        item_layer_types?: {
            swarm_width_lanes?: unknown;
            creature_template?: ReadonlyArray<unknown>;
        };
        creature_template?: ReadonlyArray<unknown>;
    } | null | undefined;

    const hostLanes = readScalarInt(ec?.item_layer_types?.swarm_width_lanes);
    if (hostLanes !== null) return Math.max(0, hostLanes);

    const list = ec?.item_layer_types?.creature_template ?? ec?.creature_template;
    if (list) {
        for (let i = 0; i < list.length; i++) {
            const entry = list[i] as {
                item_layer_types?: { swarm_width_lanes?: unknown };
            } | string | null | undefined;
            if (!entry || typeof entry !== 'object') continue;
            if (layerListEntryUuid(entry) !== def.id) continue;
            const carrierLanes = readScalarInt(entry.item_layer_types?.swarm_width_lanes);
            if (carrierLanes !== null) return Math.max(0, carrierLanes);
        }
    }
    return def.widthLanes;
}

/** v_current / effective_v_max, clamped to [0,1]; 1 when vitality is absent (catalog compile). */
export function swarmVRatio01(entity: EncounterCreatureRow | null | undefined): number {
    // PHP-parity guard: computed_value() returns null on an absent/empty bucket
    // (→ ratio 1), but the TS computedValue() DERIVES v_current=0/effective_v_max=1
    // from an empty vitality store (deriveVitality) — which would wrongly report a
    // full-strength swarm as dead-width. Only trust the ratio when the store
    // actually carries a current-vitality key.
    const e = entity as {
        computed?: { vitality?: Record<string, unknown>; v_arr?: Record<string, unknown> };
        vitality?: Record<string, unknown>;
        v_arr?: Record<string, unknown>;
    } | null | undefined;
    const store = e?.computed?.vitality ?? e?.computed?.v_arr ?? e?.vitality ?? e?.v_arr;
    const hasCurrent = !!store && typeof store === 'object'
        && (store.v_current !== undefined || store.base_v_current !== undefined);
    if (!hasCurrent) return 1;

    const cur = parseFloat(String(computedValue(entity, 'vitality', 'v_current') ?? ''));
    const max = parseFloat(String(computedValue(entity, 'vitality', 'effective_v_max') ?? ''));
    if (!Number.isFinite(cur) || !Number.isFinite(max) || max <= 0) return 1;
    // LIVE COUNT (step 6, PHP twin ft_swarm_occupied_width_ft): the ratio carries the members still alive, so
    // q = ceil(start × ratio) = ceil(members × v/v_max) — attrition shrank v_max, the band must not snap back.
    return Math.min(1, Math.max(0, cur / max)) * swarmAliveFraction(entity);
}

/** members / members_start from the compiled row's encounter_creatures bucket; 1 when unseeded. */
export function swarmAliveFraction(entity: EncounterCreatureRow | null | undefined): number {
    const bucket = (entity as { encounter_creatures?: Record<string, unknown> } | null | undefined)?.encounter_creatures;
    const members = Number(bucket?.swarm_members);
    const start = Number(bucket?.swarm_members_start);
    if (!Number.isFinite(members) || !Number.isFinite(start) || start < 1) return 1;
    return Math.min(1, Math.max(0, members / start));
}


/** Extra lanes currently active given remaining strength. */
export function swarmActiveExtraLanes(def: SwarmTemplateDef, lanes: number, vRatio01: number): number {
    if (lanes <= 0 || def.monsterQty < 2) return 0;
    // 1e-9: a live-count ratio must not float past an integer into an extra body (PHP twin).
    const q = vRatio01 <= 0 ? 0 : Math.max(1, Math.ceil(def.monsterQty * vRatio01 - 1e-9));
    return q <= 1 ? 0 : Math.ceil(lanes * q / def.monsterQty);
}

/** Occupied token width in feet (= baseFt for non-swarms / single survivor / dead). */
export function swarmOccupiedWidthFt(
    baseFt: number,
    def: SwarmTemplateDef,
    lanes: number,
    vRatio01: number,
): number {
    return baseFt + swarmActiveExtraLanes(def, lanes, vRatio01) * Math.min(baseFt, 5);
}
